import { FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useNotify, useRefresh, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { ContactImportSchema } from "../../atomic-crm/contacts/useContactImport";
import { useContactImport } from "../../atomic-crm/contacts/useContactImport";
import {
  applyMapping,
  CONTACT_IMPORT_TARGETS,
  guessMapping,
  parseTabularFile,
  type ParsedTable,
} from "./tabularParse";

const BATCH_SIZE = 10;

type Step =
  | { name: "pick" }
  | { name: "map"; table: ParsedTable; mapping: Record<string, string> }
  | { name: "running"; total: number; done: number; errors: number }
  | { name: "complete"; total: number; errors: number };

/**
 * Import contacts from CSV or XLSX with a column-mapping step.
 *
 * The previous importer required the file's headers to already match an
 * internal schema, which real exports from other CRMs never do, the person
 * migrating in has a file with "E-mail Address" and "Organisation" and no
 * way to say what those mean. The mapping step shows THEIR headers with a
 * best-guess preselection and a preview of the first rows, so the correction
 * happens before the import, not after 900 contacts arrive half-blank.
 */
export const MappedContactImport = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const processBatch = useContactImport();
  const [step, setStep] = useState<Step>({ name: "pick" });
  const cancelled = useRef(false);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try {
      const table = await parseTabularFile(file);
      if (table.rows.length === 0) {
        notify("That file has no data rows", { type: "warning" });
        return;
      }
      setStep({ name: "map", table, mapping: guessMapping(table.headers) });
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not read that file",
        { type: "error" },
      );
    }
  };

  const runImport = async (
    table: ParsedTable,
    mapping: Record<string, string>,
  ) => {
    const rows = applyMapping(table.rows, mapping);
    cancelled.current = false;
    let done = 0;
    let errors = 0;
    setStep({ name: "running", total: rows.length, done, errors });

    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      if (cancelled.current) return;
      const batch = rows.slice(i, i + BATCH_SIZE);
      try {
        await processBatch(batch as unknown as ContactImportSchema[]);
        done += batch.length;
      } catch (error) {
        console.error("Import batch failed", error);
        errors += batch.length;
      }
      setStep({ name: "running", total: rows.length, done, errors });
    }
    setStep({ name: "complete", total: rows.length, errors });
    refresh();
  };

  const close = () => {
    cancelled.current = true;
    setStep({ name: "pick" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate("resources.contacts.import.title", {
              _: "Import contacts",
            })}
          </DialogTitle>
        </DialogHeader>

        {step.name === "pick" && (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border p-8 text-center hover:bg-accent/40">
            <FileSpreadsheet className="size-8 text-muted-foreground" />
            <span className="text-[13.5px] font-medium">
              Choose a .csv or .xlsx file
            </span>
            <span className="text-[12px] text-muted-foreground">
              You&apos;ll match its columns to contact fields next, headers
              don&apos;t need to follow any template.
            </span>
            <input
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
            />
          </label>
        )}

        {step.name === "map" && (
          <div className="flex flex-col gap-3">
            <p className="text-[12.5px] text-muted-foreground">
              {step.table.rows.length} rows found. Match your file&apos;s
              columns to contact fields, anything left as “skip” is ignored.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {CONTACT_IMPORT_TARGETS.map((target) => (
                <label
                  key={target.field}
                  className="flex flex-col gap-0.5 text-[12px] font-medium"
                >
                  {target.label}
                  <select
                    className="h-8 rounded-md border border-input bg-transparent px-2 text-[12.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={step.mapping[target.field] ?? ""}
                    onChange={(event) =>
                      setStep({
                        ...step,
                        mapping: {
                          ...step.mapping,
                          [target.field]: event.target.value,
                        },
                      })
                    }
                  >
                    <option value="">skip</option>
                    {step.table.headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <MappingPreview table={step.table} mapping={step.mapping} />

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={close}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={
                  !step.mapping.first_name &&
                  !step.mapping.last_name &&
                  !step.mapping.email_work
                }
                onClick={() => runImport(step.table, step.mapping)}
              >
                <Upload className="mr-1 size-3.5" />
                Import {step.table.rows.length} contacts
              </Button>
            </div>
          </div>
        )}

        {step.name === "running" && (
          <div className="flex flex-col items-center gap-2 py-6">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
            <p className="text-[13px]">
              Imported {step.done} of {step.total}
              {step.errors > 0 ? `, ${step.errors} failed` : ""}
            </p>
          </div>
        )}

        {step.name === "complete" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-[13.5px] font-medium">
              Imported {step.total - step.errors} of {step.total} contacts
              {step.errors > 0 ? ` (${step.errors} failed, see console)` : ""}
            </p>
            <Button size="sm" onClick={close}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** The first three rows as they will import, mistakes show here, not after. */
const MappingPreview = ({
  table,
  mapping,
}: {
  table: ParsedTable;
  mapping: Record<string, string>;
}) => {
  const mapped = applyMapping(table.rows.slice(0, 3), mapping);
  const fields = CONTACT_IMPORT_TARGETS.filter(
    (target) => mapping[target.field],
  );
  if (fields.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-md border border-border-subtle">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-border-subtle bg-muted/40 text-left">
            {fields.map((target) => (
              <th key={target.field} className="px-2 py-1 font-medium">
                {target.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mapped.map((row, index) => (
            <tr key={index} className="border-b border-border-subtle/60">
              {fields.map((target) => (
                <td
                  key={target.field}
                  className="max-w-40 truncate px-2 py-1 text-muted-foreground"
                >
                  {row[target.field] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
